import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SCHEME_MANIFESTS } from "@shared/schemes/manifests";
import type { GlobalSchemeDefault, SchemeKey } from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";
import ColorSwatch from "../components/ColorSwatch";
import SchemeThumbnail from "../components/template/SchemeThumbnail";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { TableSkeleton } from "../components/TableSkeleton";

interface Edit {
  name: string;
  palette: Record<string, string>;
}

/** Build the editable state for a template: stored global override, falling
 *  back to the code-authored manifest defaults. */
function initialEdit(key: SchemeKey, stored?: GlobalSchemeDefault): Edit {
  const m = SCHEME_MANIFESTS[key];
  const palDefaults = m.defaultTokens.palette as Record<string, string>;
  const palette: Record<string, string> = {};
  for (const { key: pk } of m.tokenSchema?.palette ?? []) {
    palette[pk] = stored?.palette?.[pk] ?? palDefaults[pk] ?? "";
  }
  return { name: stored?.name ?? m.name, palette };
}

/**
 * Global Templates - admin edits each template's default colours + name once.
 * Those defaults seed a premise's binding when it first picks the template
 * (existing premises keep their own values). The manifest stays the code-level
 * fallback; what's stored here is a diff over it.
 */
export default function GlobalTemplatesPage() {
  const [edits, setEdits] = useState<Record<string, Edit> | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    foyerApi
      .listSchemeDefaults()
      .then((list) => {
        const stored: Record<string, GlobalSchemeDefault> = {};
        for (const d of list) stored[d.key] = d;
        const init: Record<string, Edit> = {};
        for (const m of Object.values(SCHEME_MANIFESTS)) {
          init[m.key] = initialEdit(m.key, stored[m.key]);
        }
        setEdits(init);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  function setName(key: string, name: string) {
    setEdits((cur) => (cur ? { ...cur, [key]: { ...cur[key], name } } : cur));
  }
  function setColor(key: string, pk: string, v: string) {
    setEdits((cur) =>
      cur
        ? {
            ...cur,
            [key]: { ...cur[key], palette: { ...cur[key].palette, [pk]: v } },
          }
        : cur
    );
  }

  async function save(key: SchemeKey) {
    if (!edits) return;
    const m = SCHEME_MANIFESTS[key];
    const e = edits[key];
    const palDefaults = m.defaultTokens.palette as Record<string, string>;
    // Store only what differs from the manifest - keep defaults sparse.
    const paletteDiff: Record<string, string> = {};
    for (const { key: pk } of m.tokenSchema?.palette ?? []) {
      const v = e.palette[pk]?.trim();
      if (v && v !== palDefaults[pk]) paletteDiff[pk] = v;
    }
    const name =
      e.name.trim() && e.name.trim() !== m.name ? e.name.trim() : null;
    setSavingKey(key);
    try {
      await foyerApi.saveSchemeDefault(key, {
        name,
        palette: Object.keys(paletteDiff).length ? paletteDiff : null,
      });
      setToast(`Saved ${e.name.trim() || m.name}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingKey(null);
    }
  }

  if (error && !edits) {
    return (
      <PageContainer>
        <Alert severity="error">{error}</Alert>
      </PageContainer>
    );
  }
  if (!edits) {
    return (
      <PageContainer>
        <PageHeader eyebrow="Defaults" title="Templates" />
        <TableSkeleton rows={4} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader eyebrow="Defaults" title="Templates" />

      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        {Object.values(SCHEME_MANIFESTS).map((m) => {
          const e = edits[m.key];
          const paletteKeys = m.tokenSchema?.palette ?? [];
          return (
            <Card key={m.key} variant="outlined">
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <SchemeThumbnail schemeKey={m.key} />
                </Box>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {m.name}
                  </Typography>
                  <Chip
                    size="small"
                    label={m.segment === "care" ? "Care" : "Hotel"}
                    variant="outlined"
                  />
                </Stack>

                <TextField
                  size="small"
                  fullWidth
                  label="Display name"
                  value={e.name}
                  onChange={(ev) => setName(m.key, ev.target.value)}
                  sx={{ mb: 2 }}
                />

                <Typography variant="subtitle2" gutterBottom>
                  Colours
                </Typography>
                <Stack spacing={1}>
                  {paletteKeys.map(({ key: pk, label }) => (
                    <Stack
                      key={pk}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                    >
                      <ColorSwatch
                        value={e.palette[pk] ?? ""}
                        onChange={(v) => setColor(m.key, pk, v)}
                        ariaLabel={label}
                      />
                      <TextField
                        size="small"
                        label={label}
                        value={e.palette[pk] ?? ""}
                        onChange={(ev) => setColor(m.key, pk, ev.target.value)}
                        sx={{ flex: 1 }}
                      />
                    </Stack>
                  ))}
                </Stack>

                <Box sx={{ mt: 2, textAlign: "right" }}>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={savingKey === m.key}
                    onClick={() => save(m.key)}
                    data-testid={`save-template-${m.key}`}
                  >
                    {savingKey === m.key ? "Saving…" : "Save defaults"}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast(null)}
        message={toast ?? ""}
      />
      <Snackbar
        open={!!error && !!edits}
        autoHideDuration={4000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
